<script setup>
import { computed } from 'vue';
import FormSelect from 'v3/components/Form/Select.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  value: {
    type: String,
    default: 'default',
  },
  label: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['change']);
const { t } = useI18n();

/**
 * Signature type options with their values
 * @type {Object}
 */
const SIGNATURE_TYPE_OPTIONS = {
  BOTTOM: 'bottom',
  TOP: 'top',
};

/**
 * Array of font size option keys
 * @type {Array<string>}
 */
const SIGNATURE_TYPE_NAMES = Object.keys(SIGNATURE_TYPE_OPTIONS);

/**
 * Get signature type label translation key
 *
 * @param {string} name - Signature type name
 * @returns {string} Translation key
 */
const getSignatureTypeLabelKey = name =>
  `PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE.SIGNATURE_TYPE.OPTIONS.${name}`;

/**
 * Create signature type option object
 *
 * @param {Function} t - Translation function
 * @param {string} name - Signature type name
 * @returns {Object} Signature type option with value and label
 */
const createSignatureTypeOption = (tr, name) => ({
  value: SIGNATURE_TYPE_OPTIONS[name],
  label: tr(getSignatureTypeLabelKey(name)),
});

/**
 * Signature type options for select dropdown
 * @type {Array<{value: string, label: string}>}
 */
const signatureTypeOptions = computed(() =>
  SIGNATURE_TYPE_NAMES.map(name => createSignatureTypeOption(t, name))
);

const selectedValue = computed({
  get: () => props.value,
  set: value => {
    emit('change', value);
  },
});
</script>

<template>
  <div class="flex gap-2 justify-between w-full items-start">
    <div>
      <label class="text-n-gray-12 font-medium leading-6 text-sm">
        {{ label }}
      </label>
      <p class="text-n-gray-11">
        {{ description }}
      </p>
    </div>
    <FormSelect
      v-model="selectedValue"
      name="signatureType"
      spacing="compact"
      class="min-w-28 mt-px"
      :value="selectedValue"
      :options="signatureTypeOptions"
      label=""
    >
      <option
        v-for="option in signatureTypeOptions"
        :key="option.value"
        :value="option.value"
        :selected="option.value === selectedValue"
      >
        {{ option.label }}
      </option>
    </FormSelect>
  </div>
</template>
